export default function Mozambique() {
  return (
    <section className="relative py-24 px-12 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/0af0a827fa4b3fb4cf5cc0a93bad69d7.jpg"
          alt="Mozambique AI Smart Port Infrastructure"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/90 via-stone-800/85 to-stone-900/80"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-10 h-1 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full"></div>
            <span className="text-teal-400 text-sm font-bold tracking-wide uppercase">
              Flagship International Deployment
            </span>
            <div className="w-10 h-1 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full"></div>
          </div>

          <h2 className="text-stone-100 font-black text-5xl leading-tight mb-5">
            Mozambique — AI Smart Port &
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-400">
              ASU Integration Complex
            </span>
          </h2>

          <p className="text-stone-300 text-lg leading-relaxed max-w-4xl mx-auto">
            SkyForge is developing a combined Artificial Intelligence Smart Port and Air Separation Unit production complex within Mozambique's emerging maritime trade corridor.
          </p>
        </div>

        {/* Main Description Card */}
        <div className="bg-stone-100/95 backdrop-blur-sm rounded-3xl p-10 mb-12 shadow-2xl border border-stone-200">
          <p className="text-stone-700 text-base leading-relaxed mb-5">
            This dual-infrastructure deployment is designed to support industrial expansion, maritime fueling operations, medical gas supply, and export-based cryogenic distribution throughout Southern Africa and international shipping routes.
          </p>
          <p className="text-stone-600 text-base leading-relaxed">
            The Mozambique platform represents SkyForge's flagship international infrastructure model — combining smart trade logistics with industrial gas production at port scale.
          </p>
        </div>

        {/* Infrastructure Capabilities Grid */}
        <div className="grid grid-cols-3 gap-6">
          {/* AI-Driven Cargo Logistics */}
          <div className="bg-gradient-to-br from-stone-100/95 to-teal-50/95 backdrop-blur-sm border border-teal-200 rounded-2xl p-8 hover:shadow-2xl transition-all cursor-pointer">
            <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-5 shadow-md">
              <i className="ri-cpu-line text-2xl text-teal-600"></i>
            </div>
            <h3 className="text-stone-800 font-bold text-lg mb-3">
              AI-Driven Cargo Logistics Automation
            </h3>
            <p className="text-stone-600 text-sm leading-relaxed">
              Advanced artificial intelligence systems managing port operations, cargo routing, and supply chain optimization for maximum efficiency.
            </p>
          </div>

          {/* Cryogenic Fueling */}
          <div className="bg-gradient-to-br from-stone-100/95 to-cyan-50/95 backdrop-blur-sm border border-cyan-200 rounded-2xl p-8 hover:shadow-2xl transition-all cursor-pointer">
            <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-5 shadow-md">
              <i className="ri-ship-line text-2xl text-cyan-600"></i>
            </div>
            <h3 className="text-stone-800 font-bold text-lg mb-3">
              Cryogenic Fueling & Maritime Supply
            </h3>
            <p className="text-stone-600 text-sm leading-relaxed">
              On-site cryogenic fueling infrastructure supporting maritime vessels, industrial operations, and regional distribution networks.
            </p>
          </div>

          {/* LOX, LIN, LAR Production */}
          <div className="bg-gradient-to-br from-stone-100/95 to-teal-50/95 backdrop-blur-sm border border-teal-200 rounded-2xl p-8 hover:shadow-2xl transition-all cursor-pointer">
            <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-5 shadow-md">
              <i className="ri-flask-line text-2xl text-teal-600"></i>
            </div>
            <h3 className="text-stone-800 font-bold text-lg mb-3">
              LOX, LIN, and LAR Production
            </h3>
            <p className="text-stone-600 text-sm leading-relaxed">
              Full-scale Air Separation Unit producing Liquid Oxygen, Liquid Nitrogen, and Liquid Argon for industrial, medical, and export markets.
            </p>
          </div>

          {/* Export-Grade Storage */}
          <div className="bg-gradient-to-br from-stone-100/95 to-cyan-50/95 backdrop-blur-sm border border-cyan-200 rounded-2xl p-8 hover:shadow-2xl transition-all cursor-pointer">
            <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-5 shadow-md">
              <i className="ri-database-2-line text-2xl text-cyan-600"></i>
            </div>
            <h3 className="text-stone-800 font-bold text-lg mb-3">
              Export-Grade Storage Terminals
            </h3>
            <p className="text-stone-600 text-sm leading-relaxed">
              Large-scale cryogenic storage facilities designed for bulk distribution, export operations, and regional supply chain integration.
            </p>
          </div>

          {/* Integrated Port Energy */}
          <div className="bg-gradient-to-br from-stone-100/95 to-teal-50/95 backdrop-blur-sm border border-teal-200 rounded-2xl p-8 hover:shadow-2xl transition-all cursor-pointer">
            <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-5 shadow-md">
              <i className="ri-flashlight-line text-2xl text-teal-600"></i>
            </div>
            <h3 className="text-stone-800 font-bold text-lg mb-3">
              Integrated Port Energy Systems
            </h3>
            <p className="text-stone-600 text-sm leading-relaxed">
              Comprehensive energy infrastructure supporting port operations, ASU production, and sustainable power generation systems.
            </p>
          </div>

          {/* Strategic Impact */}
          <div className="bg-gradient-to-br from-stone-100/95 to-cyan-50/95 backdrop-blur-sm border border-cyan-200 rounded-2xl p-8 hover:shadow-2xl transition-all cursor-pointer">
            <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-5 shadow-md">
              <i className="ri-global-line text-2xl text-cyan-600"></i>
            </div>
            <h3 className="text-stone-800 font-bold text-lg mb-3">
              Southern Africa Trade Gateway
            </h3>
            <p className="text-stone-600 text-sm leading-relaxed">
              Strategic positioning within emerging maritime corridors connecting Southern Africa to international shipping routes and global markets.
            </p>
          </div>
        </div>

        {/* Bottom Highlight Banner */}
        <div className="mt-12 bg-gradient-to-r from-teal-500/90 to-cyan-500/90 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-teal-300">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-bold text-xl mb-2">
                Flagship International Infrastructure Model
              </h3>
              <p className="text-teal-50 text-sm">
                Combining smart trade logistics with industrial gas production at port scale — setting the standard for integrated infrastructure deployment worldwide.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-ship-line text-2xl text-white"></i>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-building-4-line text-2xl text-white"></i>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-cpu-line text-2xl text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
